import { Arrange, SvgNotes, SvgSparkle, Tooltip } from '@loomhq/lens';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  updateFillerWordHighlight,
  updateIsFillerRemovalFTUX,
} from 'actions/videoPlayer';
import {
  ROLLOUT_TRANSCRIPTION_BUSINESS,
  ROLLOUT_TRANSCRIPTION_ENTERPRISE,
  ROLLOUT_TRANSCRIPT_DISPLAY,
  STORE_FILLER_WORD_RANGES,
} from 'constants/featureFlag';
import { REVIEW_TRANSCRIPT, TRANSCRIPT } from 'constants/menuOptions';
import { TRANSCRIPTION_PAYWALL } from 'constants/scopes';
import { handleToggleTranscriptCreator } from 'creators/shareVideo';
import {
  getShowReviewTranscriptButtonHightlight,
  setShowReviewTranscriptButtonHightlight,
} from 'utilities/localStorage';

import { useFlagIsActivated } from 'hooks/featureFlag';
import { hasScope } from 'selectors/scopes';
import {
  selectShowTranscriptButton,
  selectTranscriptionStatus,
} from 'selectors/transcripts';
import {
  selectCurrentUserIsOwner,
  selectTranscriptVisible,
} from 'selectors/videoPlayer';

import CollapsibleSideButton from '../collapsible-side-button';
import { SHORT_DURATION } from '../../../../../shared/constants/videoTranscript';

import UpgradeTooltip from 'components/upgrade-tooltip';

const TranscriptOverlayButton = ({
  isCollapsed,
  onOverlayButtonClick = () => null,
}) => {
  const dispatch = useDispatch();
  const transcriptStatus = useSelector(selectTranscriptionStatus);
  const showTranscriptButton = useSelector(selectShowTranscriptButton);
  const hasTranscriptionPaywall = useSelector(state =>
    hasScope(state, TRANSCRIPTION_PAYWALL)
  );

  const currentUserIsOwner = useSelector(selectCurrentUserIsOwner);

  const shouldShowFillerWordRemoval = useFlagIsActivated({
    flag: STORE_FILLER_WORD_RANGES,
    activationValues: [true],
  });
  const rolloutTranscriptDisplay = useFlagIsActivated({
    flag: ROLLOUT_TRANSCRIPT_DISPLAY,
    activationValues: [true],
  });
  const rolloutTranscriptionBusiness = useFlagIsActivated({
    flag: ROLLOUT_TRANSCRIPTION_BUSINESS,
    activationValues: [true],
  });
  const rolloutTranscriptionEnterprise = useFlagIsActivated({
    flag: ROLLOUT_TRANSCRIPTION_ENTERPRISE,
    activationValues: [true],
  });

  const isTranscriptVisible = useSelector(selectTranscriptVisible);
  const showReviewTranscriptButtonHighlight =
    getShowReviewTranscriptButtonHightlight();

  const showOverlayFTUX =
    showReviewTranscriptButtonHighlight && !isTranscriptVisible;

  const transcriptButtonText =
    shouldShowFillerWordRemoval && currentUserIsOwner
      ? REVIEW_TRANSCRIPT
      : TRANSCRIPT;

  // Show the paywall if the video doesn't have a transcript but
  // could have been eligible for a transcript based on the user's role
  const transcriptionEnabled =
    rolloutTranscriptDisplay ||
    rolloutTranscriptionBusiness ||
    rolloutTranscriptionEnterprise;

  const showTranscriptPaywall =
    !showTranscriptButton &&
    hasTranscriptionPaywall &&
    currentUserIsOwner &&
    transcriptionEnabled;

  if (showTranscriptPaywall) {
    return (
      <UpgradeTooltip
        placement="leftCenter"
        title="View a transcript of your Loom"
      >
        <CollapsibleSideButton
          icon={<SvgNotes />}
          isCollapsed={isCollapsed}
          isUpgrade
        >
          <div>
            {transcriptButtonText}
            &lrm;
          </div>
        </CollapsibleSideButton>
      </UpgradeTooltip>
    );
  }

  const onTranscriptButtonClick = () => {
    onOverlayButtonClick();
    dispatch(handleToggleTranscriptCreator());

    if (showOverlayFTUX) {
      setShowReviewTranscriptButtonHightlight(false);
      dispatch(updateIsFillerRemovalFTUX(true));
      dispatch(updateFillerWordHighlight(true));
    }
  };

  if (showTranscriptButton && transcriptStatus === SHORT_DURATION) {
    return (
      <Tooltip content="Transcript not available" placement="leftCenter">
        <CollapsibleSideButton
          onClick={onOverlayButtonClick}
          icon={<SvgNotes />}
          isCollapsed={isCollapsed}
        >
          <div>
            {transcriptButtonText}
            &lrm;
          </div>
        </CollapsibleSideButton>
      </Tooltip>
    );
  }

  return (
    showTranscriptButton && (
      <CollapsibleSideButton
        onClick={onTranscriptButtonClick}
        data-testid="toggle-transcript-button"
        icon={<SvgNotes />}
        isCollapsed={isCollapsed}
        className={showOverlayFTUX ? 'highlightFTUX' : ''}
        leftIcon={showOverlayFTUX && <SvgSparkle />}
        leftIconColor={showOverlayFTUX && 'white'}
      >
        <Arrange gap="small" alignItems="center">
          <div>
            {transcriptButtonText}
            &lrm;
          </div>
        </Arrange>
      </CollapsibleSideButton>
    )
  );
};

export default TranscriptOverlayButton;
